import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  Pill, CheckCircle2, Clock, AlertTriangle,
  RefreshCcw, Sun, Sunset, Moon, Filter
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const trafficConfig = {
  green: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2, label: "A tiempo" },
  yellow: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: Clock, label: "En ventana" },
  red: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-600", icon: AlertTriangle, label: "Atrasado" },
};

const shiftRanges = {
  manana: { start: 6, end: 14, label: "Mañana (06-14)", icon: Sun },
  tarde: { start: 14, end: 22, label: "Tarde (14-22)", icon: Sunset },
  noche_a: { start: 22, end: 24 },
  noche_b: { start: 0, end: 6 },
};

function itemInShift(scheduledTime, shift) {
  if (shift === "all") return true;
  try {
    const hour = parseInt(scheduledTime.split(":")[0], 10);
    if (shift === "manana") return hour >= 6 && hour < 14;
    if (shift === "tarde") return hour >= 14 && hour < 22;
    if (shift === "noche") return hour >= 22 || hour < 6;
  } catch { return true; }
  return true;
}

export default function MedicationRoundPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colorFilter, setColorFilter] = useState("all");
  const [shiftFilter, setShiftFilter] = useState("all");
  const [adminDialog, setAdminDialog] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [doubleValidation, setDoubleValidation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRound = useCallback(async () => {
    try {
      const r = await api.get("/medication-round");
      setItems(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRound(); }, [fetchRound]);

  const filteredItems = items.filter(i => {
    if (colorFilter !== "all" && i.traffic_light !== colorFilter) return false;
    if (!itemInShift(i.scheduled_time, shiftFilter)) return false;
    return true;
  });

  const handleAdminister = async () => {
    if (!adminDialog) return;
    setSubmitting(true);
    try {
      await api.post("/medication/administer", {
        prescription_id: adminDialog.prescription_id,
        resident_id: adminDialog.resident_id,
        medication_name: adminDialog.medication_name,
        scheduled_time: adminDialog.scheduled_time,
        dose_given: adminDialog.dosage,
        route: adminDialog.route,
        status: "administered",
        notes: adminNotes,
        double_validation_by: adminDialog.type === "psicotropico" ? doubleValidation : null,
      });
      toast.success(`${adminDialog.medication_name} administrado correctamente`);
      setAdminDialog(null);
      setAdminNotes("");
      setDoubleValidation("");
      fetchRound();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar administración");
    }
    setSubmitting(false);
  };

  const counts = { red: 0, yellow: 0, green: 0 };
  items.forEach(i => { if (counts[i.traffic_light] !== undefined) counts[i.traffic_light]++; });

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="medication-round-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Ronda de Medicación</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {new Date().toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={shiftFilter} onValueChange={setShiftFilter}>
            <SelectTrigger className="rounded-xl h-10 bg-white border-slate-200 w-[180px] text-sm" data-testid="shift-filter-select">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los turnos</SelectItem>
              <SelectItem value="manana">Mañana (06-14)</SelectItem>
              <SelectItem value="tarde">Tarde (14-22)</SelectItem>
              <SelectItem value="noche">Noche (22-06)</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setLoading(true); fetchRound(); }} variant="outline" className="rounded-xl px-5 h-10 border-slate-200 text-slate-600 hover:bg-slate-50 text-sm" data-testid="refresh-round-btn">
            <RefreshCcw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
        </div>
      </div>

      {/* Semáforo Summary */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4">
        {[
          { key: "red", label: "Atrasados", icon: AlertTriangle },
          { key: "yellow", label: "En ventana", icon: Clock },
          { key: "green", label: "A tiempo / Hecho", icon: CheckCircle2 },
        ].map(s => {
          const cfg = trafficConfig[s.key];
          return (
            <Card
              key={s.key}
              className={`rounded-2xl cursor-pointer transition-all duration-200 hover:-translate-y-[1px] ${
                colorFilter === s.key ? `${cfg.border} ${cfg.bg} border` : "border-slate-100 shadow-sm hover:shadow-md"
              }`}
              onClick={() => setColorFilter(colorFilter === s.key ? "all" : s.key)}
              data-testid={`filter-${s.key}`}
            >
              <CardContent className="p-4 lg:p-5 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center`}>
                  <s.icon className={`w-5 h-5 ${cfg.text} ${s.key === "red" ? "animate-pulse-soft" : ""}`} strokeWidth={1.8} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${cfg.text}`}>{counts[s.key]}</p>
                  <p className="text-xs text-slate-400 font-medium">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Active shift filter indicator */}
      {shiftFilter !== "all" && (
        <div className="flex items-center gap-2">
          <Badge className="rounded-full bg-violet-50 text-violet-700 border-0 text-xs font-semibold px-3 py-1">
            Filtrando por turno: {shiftFilter === "manana" ? "Mañana" : shiftFilter === "tarde" ? "Tarde" : "Noche"}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setShiftFilter("all")} className="text-xs text-slate-400 hover:text-slate-700 h-7 px-2">
            Limpiar
          </Button>
        </div>
      )}

      {/* Medication Items */}
      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando ronda de medicación...</p>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16">
          <Pill className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-slate-400 font-medium">No hay medicaciones{colorFilter !== "all" ? ` con estado "${trafficConfig[colorFilter]?.label}"` : ""}{shiftFilter !== "all" ? ` para el turno seleccionado` : ""}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item, idx) => {
            const cfg = trafficConfig[item.traffic_light] || trafficConfig.green;
            const isAdministered = item.status === "administered";

            return (
              <Card
                key={`${item.prescription_id}-${item.scheduled_time}-${idx}`}
                className={`rounded-2xl transition-all duration-200 ${
                  isAdministered ? "border-slate-100 opacity-60 shadow-none" : `border ${cfg.border} ${cfg.bg} shadow-sm`
                }`}
                data-testid={`mar-item-${idx}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isAdministered ? "bg-emerald-50" : cfg.bg}`}>
                      {isAdministered
                        ? <CheckCircle2 className="w-6 h-6 text-emerald-600" strokeWidth={1.8} />
                        : <cfg.icon className={`w-6 h-6 ${cfg.text} ${item.traffic_light === "red" ? "animate-pulse-soft" : ""}`} strokeWidth={1.8} />
                      }
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{item.medication_name}</h3>
                        <Badge className={`rounded-full text-[10px] font-semibold border-0 ${
                          item.type === "psicotropico" ? "bg-fuchsia-50 text-fuchsia-700" : "bg-violet-50 text-violet-700"
                        }`}>
                          {item.type === "cronico" ? "Crónico" : item.type === "psicotropico" ? "Psicotrópico" : "SOS"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{item.resident_name} {item.room_number ? `— Hab. ${item.room_number}` : ""}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                        <span>{item.dosage} — {item.route}</span>
                        <span className="font-semibold text-slate-600">{item.scheduled_time} hs</span>
                      </div>
                    </div>

                    {!isAdministered ? (
                      <Button
                        onClick={() => setAdminDialog(item)}
                        className="rounded-xl px-6 h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all duration-200 min-w-[130px] text-sm"
                        data-testid={`administer-btn-${idx}`}
                      >
                        Administrar
                      </Button>
                    ) : (
                      <Badge className="rounded-full px-3 py-1.5 bg-emerald-50 text-emerald-700 border-0 font-semibold text-xs">
                        Administrado
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Administration Dialog */}
      <Dialog open={!!adminDialog} onOpenChange={() => { setAdminDialog(null); setAdminNotes(""); setDoubleValidation(""); }}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Registrar Administración</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Confirmar la administración del medicamento</DialogDescription>
          </DialogHeader>
          {adminDialog && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-900">{adminDialog.medication_name} — {adminDialog.dosage}</p>
                <p className="text-sm text-slate-500">{adminDialog.resident_name} — {adminDialog.scheduled_time} hs</p>
                <p className="text-sm text-slate-500">Vía: {adminDialog.route}</p>
              </div>

              {adminDialog.type === "psicotropico" && (
                <div className="space-y-2 p-4 bg-fuchsia-50 rounded-xl border border-fuchsia-100">
                  <Label className="font-medium text-sm text-fuchsia-700">Doble Validación (Psicotrópico)</Label>
                  <Input
                    value={doubleValidation}
                    onChange={e => setDoubleValidation(e.target.value)}
                    placeholder="Nombre del segundo profesional"
                    className="rounded-xl h-10 bg-white border-fuchsia-200 focus:border-fuchsia-400"
                    required
                    data-testid="double-validation-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="font-medium text-sm text-slate-700">Observaciones</Label>
                <Textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Notas opcionales..."
                  className="rounded-xl bg-slate-50 min-h-[60px] border-slate-200 focus:border-violet-500"
                  data-testid="admin-notes-input"
                />
              </div>

              <Button
                onClick={handleAdminister}
                disabled={submitting || (adminDialog.type === "psicotropico" && !doubleValidation)}
                className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all"
                data-testid="confirm-administer-btn"
              >
                {submitting ? "Registrando..." : "Confirmar Administración"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
